/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { a11y } from './a11y.js';
import { browser } from './browser.js';
import { getConfig } from './config.js';
import { contentPreprocessors } from './content-preprocessors.js';
import { emit } from './events.js';
import { isExternalLink } from './links.js';
import { KontextMap } from './map.js';
import { stopAudioVideo } from './media.js';
import { stripHTML, translate } from './text.js';
import { applyStyleSet, limit, numStr, smartStripLineBreaks } from './util.js';

/*
 * The two ways a map point opens: a panel that slides in from the side, and
 * a small window anchored to the point itself.
 *
 * The sharing panel is supplied rather than imported. It is configured from
 * the map's own settings, so the page builds it once and hands it over; a
 * direct import here would mean this module deciding which services a
 * tenant offers.
 */

let socialPanel = null;

/**
 * Give the popups a sharing panel to render.
 *
 * @param {object|null} social - A `Social` instance, or nothing to hide it.
 */
export function setSocial(social) {
    socialPanel = social;
}

function getSocial() {
    return socialPanel;
}

/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */


// The namespace is created by `map.js`; this module extends it.

/** @typedef {import('./types.d.js').Edge} Edge */
/** @typedef {import('./types.d.js').Node} Node */
/** @typedef {import('./types.d.js').MapElement} MapElement */
/** @typedef {import('./types.d.js').MapElements} MapElements */
/** @typedef {import('./types.d.js').MapManager} MapManager */

(() => {
    /* -------------------------------------------------------
     * Small shared helpers
     * -----------------------------------------------------*/

    /**
     * Build previous/next navigation list.
     * @param {HTMLElement} container
     * @param {Node | undefined} prevItem
     * @param {Node | undefined} nextItem
     * @param {string} language
     * @param {(s:string)=>string} unwrap
     */
    function buildPrevNextNav(container, prevItem, nextItem, language, unwrap) {
        container.innerHTML = '';

        if (!prevItem && !nextItem) {
            container.style.display = 'none';
            return;
        }
        container.style.display = '';

        const ul = document.createElement('ul');
        container.appendChild(ul);

        if (prevItem) {
            const li = document.createElement('li');
            li.className = 'prev';
            const a = document.createElement('a');
            a.setAttribute('href', `#p=${prevItem.id}`);

            const icon = document.createElement('span');
            icon.textContent = '<';
            const text = document.createElement('span');
            text.innerHTML = unwrap(
                translate(prevItem.content.title, language)
            );

            a.appendChild(icon);
            a.appendChild(text);
            li.appendChild(a);
            ul.appendChild(li);
        }

        if (nextItem) {
            const li = document.createElement('li');
            li.className = 'next';
            const a = document.createElement('a');
            a.setAttribute('href', `#p=${nextItem.id}`);

            const text = document.createElement('span');
            text.innerHTML = unwrap(
                translate(nextItem.content.title, language)
            );
            const icon = document.createElement('span');
            icon.textContent = '>';

            a.appendChild(text);
            a.appendChild(icon);
            li.appendChild(a);
            ul.appendChild(li);
        }
    }

    /**
     * Shared renderer for the social media panel (PopupPanel and PopupManager).
     * @param {HTMLElement} container
     * @param {string[]} services
     * @param {string} mapUrlBase
     * @param {Node} node
     * @param {string} language
     * @param {{socialMedia: string}} labels
     */
    function renderSocialPanel(
        container,
        services,
        mapUrlBase,
        node,
        language,
        labels
    ) {
        if (!container) return;
        container.innerHTML = '';
        const h3 = document.createElement('h3');
        h3.textContent = labels.socialMedia;
        container.appendChild(h3);
        const ul = document.createElement('ul');
        container.appendChild(ul);
        services.forEach((service) => {
            const li = document.createElement('li');
            li.className = 'social-share-link';
            const a = document.createElement('a');
            const target = service === 'email' ? '_top' : '_blank';
            const linkUrl = getSocial().getServiceLinkUrl(
                service,
                KontextMap._getPopupUrl(mapUrlBase, node.id),
                translate(node.content.title, language),
                stripHTML(
                    translate(node.content.content, language)
                )
            );
            a.setAttribute('href', linkUrl);
            a.setAttribute('target', target);
            a.setAttribute('alt', getSocial().getDisplayName(service));
            a.setAttribute(
                'aria-label',
                getSocial().getDisplayName(service)
            );
            a.innerHTML = getSocial().getServiceIconTag(service);
            li.appendChild(a);
            ul.appendChild(li);
        });
    }

    /**
     * Hydrate all .lottie-frame elements inside a container.
     * The TinyMCE editor produces:
     *   <div class="lottie-frame" data-lottie-src="...">
     *     <img src="lottie-placeholder.svg" />
     *   </div>
     * This replaces the placeholder with a live lottie-web animation.
     * @param {HTMLElement} container
     */
    function hydrateLottieFrames(container) {
        if (!window.lottie) return;
        container
            .querySelectorAll('.lottie-frame[data-lottie-src]')
            .forEach((frame) => {
                const src = frame.getAttribute('data-lottie-src');
                if (!src) return;
                frame.innerHTML = '';
                // Loading with autoplay off still renders the first frame,
                // so the illustration is present but does not loop at a
                // reader who asked for stillness.
                const animate = !a11y?.prefersReducedMotion();
                if (
                    KontextMap.isDotLottieUrl?.(src) &&
                    window.loadDotLottieAnimation
                ) {
                    window.loadDotLottieAnimation(src).then((animationData) => {
                        window.lottie.loadAnimation({
                            container: frame,
                            renderer: 'svg',
                            loop: animate,
                            autoplay: animate,
                            animationData
                        });
                    });
                } else {
                    window.lottie.loadAnimation({
                        container: frame,
                        renderer: 'svg',
                        loop: animate,
                        autoplay: animate,
                        path: src
                    });
                }
            });
    }

    /** Shared helper for external content assignment with preprocessors. */
    function renderExternalContent(container, html, preprocessorOptions = {}) {
        container.classList.add('popup-external-content');
        container.innerHTML = contentPreprocessors?.applyToHTML?.(
            html,
            preprocessorOptions
        );
    }

    /* -------------------------------------------------------
     * Style helpers
     * -----------------------------------------------------*/
    KontextMap._getPopupStyle = (popupDesign, contentType) => {
        const contentDesign = popupDesign[contentType] || {};
        const cssProps = {};
        const cssVars = {};
        if (contentDesign) {
            if (contentDesign.font_color)
                cssProps.color = contentDesign.font_color;
            if (contentDesign.font_url)
                cssProps.fontFamily = contentDesign.font_url;
            if (contentDesign.font_size)
                cssProps.fontSize = `${contentDesign.font_size}px`;
            if (contentDesign.font_style)
                cssProps.fontStyle = `${contentDesign.font_style}`;
            if (contentDesign.font_weight)
                cssProps.fontWeight = `${contentDesign.font_weight}`;
            if (contentDesign.line_height)
                cssProps.lineHeight = `${contentDesign.line_height}`;
            if (contentDesign.paragraph_spacing)
                cssVars['--paragraph-spacing'] =
                    `${contentDesign.paragraph_spacing}em`;
        }
        return { cssProps, cssVars };
    };

    /**
     * Rewrite internal URL.
     * @param {string} href
     * @param {string} languageParam
     * @param {string} previewParam
     * @returns {string}
     */
    KontextMap._sanitizeInternalLink = (
        href,
        languageParam,
        previewParam
    ) => {
        if (isExternalLink(href)) {
            return href;
        }
        try {
            const url = new URL(href, window.location);
            const params = url.searchParams;
            if (!languageParam) {
                params.delete('lang');
            } else if (languageParam && !params.has('lang')) {
                params.append('lang', languageParam);
            }
            if (!previewParam) {
                params.delete('preview');
            } else if (previewParam && !params.has('preview')) {
                params.append('preview', '1');
            }
            url.searchParams.sort();
            return url.toString();
        } catch (e) {
            console.error(e);
            return href;
        }
    };

    /* -------------------------------------------------------
     * Popup content builder
     * -----------------------------------------------------*/
    /**
     * Utility to create DOM structure for a popup
     * @param {Element} contentElem
     * @param {Node} node
     * @param {Array<Node>} adjacentNodes
     * @param {string} language
     * @param {DOMStringMap} labels
     * @param {boolean} showTitle
     * @param {boolean} showDate
     * @param {boolean} showConnectedNodes
     * @param {boolean} showSocialMediaPanel
     */
    KontextMap._createPopupMarkup = (
        contentElem,
        node,
        adjacentNodes,
        language,
        labels,
        showTitle,
        showDate,
        showConnectedNodes,
        showSocialMediaPanel,
        preprocessorOptions = {}
    ) => {
        const urlParams = new URL(location.href).searchParams;
        const languageParam = urlParams.get('lang');
        // The resolved state rather than the address: a published map must
        // not rewrite its internal links to ask for a draft.
        const previewParam = getConfig()?.map?.isPreview ? '1' : null;

        // Install a temporary sanitizer so the global preprocessor can apply it.
        const unsetSanitizer =
            contentPreprocessors?.setLinkSanitizer?.(function () {
                try {
                    this.href = KontextMap._sanitizeInternalLink(
                        this.href,
                        languageParam,
                        previewParam
                    );
                } catch (err) {
                    console.error('Internal link sanitize failed:', err);
                }
            });

        const nodeContent = node.content || {};

        // Title
        if (showTitle) {
            const title = smartStripLineBreaks(
                translate(nodeContent.title, language)
            );
            const h1 = document.createElement('h1');
            h1.innerHTML = title;
            contentElem.appendChild(h1);
        }

        // Main content
        const main = document.createElement('div');
        main.className = 'popup-content-main';
        main.innerHTML =
            contentPreprocessors?.applyToHTML?.(
                translate(nodeContent.content, language),
                preprocessorOptions
            ) || '';
        contentElem.appendChild(main);

        // Marginal helper
        const createMarginal = (html, extraClass) => {
            if (!html) return;
            const div = document.createElement('div');
            div.className = `popup-content-marginal ${extraClass}`;
            div.innerHTML = contentPreprocessors?.applyToHTML?.(
                html,
                preprocessorOptions
            );
            contentElem.appendChild(div);
        };

        createMarginal(
            translate(nodeContent.marginal_content_1, language),
            'popup-articles'
        );
        createMarginal(
            translate(nodeContent.marginal_content_2, language),
            'popup-links'
        );

        // Modification date
        const modificationDateTimeStringUTC = nodeContent.updated_at;
        if (showDate && modificationDateTimeStringUTC) {
            const date = new Date(modificationDateTimeStringUTC);
            if (!Number.isNaN(date.getTime())) {
                const locale =
                    document.documentElement.getAttribute('lang') || 'en';
                const opts = {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                };
                const localized = date.toLocaleString(locale, opts);
                const info = document.createElement('div');
                info.className = 'popup-content-marginal popup-content-info';
                info.innerHTML = `${labels.contentLastUpdate} ${localized}`;
                contentElem.appendChild(info);
            }
        }

        // Connected nodes
        if (showConnectedNodes && adjacentNodes && adjacentNodes.length) {
            const connected = adjacentNodes.filter((n) => {
                const c = n.content;
                return (
                    translate(c.content, language) ||
                    translate(c.marginal_content_1, language) ||
                    translate(c.marginal_content_2, language)
                );
            });
            if (connected.length) {
                const localeName = language.replace('_', '-');
                const cmp = new Intl.Collator(localeName).compare;
                connected.sort((a, b) => {
                    const ta = smartStripLineBreaks(
                        translate(a.content.title, language)
                    );
                    const tb = smartStripLineBreaks(
                        translate(b.content.title, language)
                    );
                    return cmp(ta, tb);
                });

                const wrap = document.createElement('div');
                wrap.className = 'popup-connections';

                const h3 = document.createElement('h3');
                h3.textContent = labels.connections;
                wrap.appendChild(h3);

                const ul = document.createElement('ul');
                for (const c of connected) {
                    const t =
                        smartStripLineBreaks(
                            translate(c.content.title, language)
                        ) || '';
                    if (!t.trim()) continue;
                    const li = document.createElement('li');
                    const a = document.createElement('a');
                    a.setAttribute('href', `#p=${c.id}`);
                    a.textContent = t;
                    li.appendChild(a);
                    ul.appendChild(li);
                }
                wrap.appendChild(ul);
                contentElem.appendChild(wrap);
            }
        }

        // Social placeholder
        if (showSocialMediaPanel) {
            const sm = document.createElement('div');
            sm.className = 'popup-social';
            contentElem.appendChild(sm);
        }

        // Restore previous sanitizer
        if (typeof unsetSanitizer === 'function') unsetSanitizer();
    };

    /* -------------------------------------------------------
     * URL + graph helpers
     * -----------------------------------------------------*/
    /**
     * @param {string} mapHref
     * @param {number} nodeId
     * @returns {`${string}?r=${string}`|`${string}/popup/${string}`}
     */
    KontextMap._getPopupUrl = (mapHref, nodeId) => {
        const mapUrl = new URL(mapHref);
        if (mapUrl.origin === window.origin) {
            return `${mapHref}/popup/${nodeId}`;
        } else {
            const redirect = `${mapHref}#p=${nodeId}`;
            const popupUrl = `${window.location.origin + window.location.pathname}/popup/${nodeId}`;
            return `${popupUrl}?r=${encodeURIComponent(redirect)}`;
        }
    };

    /**
     * Build an adjacency map of node IDs to sets of neighbor IDs.
     * @param {{edges: Array<Edge>}} mapData
     * @returns {Map<string, Set<string>>}
     */
    KontextMap._adjacencyMap = (mapData) => {
        const m = new Map();
        (mapData.edges || []).forEach((edge) => {
            const fromId = String(edge.node.from_id);
            const toId = String(edge.node.to_id);
            if (!m.has(fromId)) m.set(fromId, new Set());
            if (!m.has(toId)) m.set(toId, new Set());
            m.get(fromId).add(toId);
            m.get(toId).add(fromId);
        });
        return m;
    };

    /* =======================================================
     * PopupPanel (side panel manager)
     * =====================================================*/
    /**
     * Create a side panel manager.
     * Public methods: position, showFor, isVisible, isInTransition, hide,
     * reloadSocialMedia, setSocialMediaBaseURL.
     *
     * @param {HTMLElement} viewportElem
     * @param {MapManager} map
     * @param {MapElements} mapData
     * @param {string} language
     * @constructor
     */
    KontextMap.PopupPanel = function (
        viewportElem,
        map,
        mapData,
        language,
        labels,
        options
    ) {
        const manager = /** @type {any} */ (this);

        const popupDesign = mapData.design?.popups || {};
        const popupTypeMap = new Map([
            ['popup-panel', 'small'],
            ['popup-panel-large', 'large']
        ]);

        /** @type {HTMLElement} */
        const viewport = viewportElem;

        const showConnectedNodesHighlighted = JSON.parse(
            options.showConnectedNodesHighlighted
        );
        const showConnectedNodesList = JSON.parse(
            options.showConnectedNodesList
        );

        const showSocialMediaPanel = getSocial()?.isEnabled();
        const socialMediaServices = showSocialMediaPanel
            ? getSocial().enabledServices()
            : [];
        let socialMediaMapUrl =
            window.location.origin + window.location.pathname;

        const showModificationDate = JSON.parse(options.showModificationDate);

        /** @type {HTMLElement} */ let panelElem;
        /** @type {HTMLElement} */ let sectionElem;
        /** @type {HTMLElement} */ let contentElem;
        /** @type {HTMLElement} */ let bottomNavElem;
        let currentPanelType;
        let movedFocusIn = false;

        const adjacentNodesById = KontextMap._adjacencyMap(mapData);

        const nodesById = new Map();
        (mapData.nodes || []).forEach((n) => nodesById.set(String(n.id), n));

        function getNode(nodeId) {
            return nodesById.get(String(nodeId));
        }
        function unwrapText(text) {
            return smartStripLineBreaks(text);
        }
        function adjacently(id) {
            return adjacentNodesById.get(id) || new Set();
        }

        function createPanel() {
            if (panelElem) panelElem.remove();

            panelElem = document.createElement('div');
            panelElem.className = 'popup popup-panel';
            panelElem.setAttribute('data-cc-external', 'placeholder');

            const section = document.createElement('section');
            sectionElem = section;
            // A dialog, but deliberately not a modal one: the panel sits
            // beside the map and the map stays operable next to it, so it
            // gets neither aria-modal nor a focus trap. The tabindex lets
            // focus land on the section itself, which makes a screen reader
            // announce the dialog and start reading its content.
            section.setAttribute('role', 'dialog');
            section.setAttribute('tabindex', '-1');
            panelElem.appendChild(section);
            ['mousedown', 'wheel', 'touchstart'].forEach((evt) =>
                section.addEventListener(evt, (e) => e.stopPropagation())
            );

            const topNav = document.createElement('nav');
            topNav.className = 'popup-navigation-top';
            section.appendChild(topNav);

            const back = document.createElement('button');
            back.type = 'button';
            back.setAttribute(
                'aria-label',
                labels.a11y?.closePopup || labels.back
            );
            back.addEventListener('click', (e) => {
                e.preventDefault();
                manager.hide();
            });
            const icon = document.createElement('i');
            icon.className = 'fas fa-times';
            icon.setAttribute('aria-hidden', 'true');
            back.appendChild(icon);
            topNav.appendChild(back);

            contentElem = document.createElement('main');
            contentElem.className = 'popup-content';
            section.appendChild(contentElem);

            bottomNavElem = document.createElement('nav');
            bottomNavElem.className = 'popup-navigation-bottom';
            applyStyleSet(
                bottomNavElem,
                KontextMap._getPopupStyle(popupDesign, 'nav')
            );
            section.appendChild(bottomNavElem);

            document.addEventListener(
                'keydown',
                (event) => {
                    if (event.key !== 'Escape' || !manager.isVisible()) {
                        return;
                    }
                    // The map binds a window-level Escape to close an embed;
                    // closing the panel must not also close the embed
                    // around it.
                    event.stopPropagation();
                    manager.hide();
                },
                true
            );

            viewport.appendChild(panelElem);
        }

        function clearContent() {
            stopAudioVideo(contentElem);
            contentElem.scrollTop = 0;
            contentElem.classList.remove('popup-external-content');
            contentElem.innerHTML = '';
        }

        /**
         * Set internal popup content for a given node.
         * @param {Node} data
         */
        function setPopupContent(data, preprocessorOptions = {}) {
            clearContent();
            const connectedNodes = Array.from(adjacently(String(data.id))).map(
                getNode
            );

            KontextMap._createPopupMarkup(
                contentElem,
                data,
                connectedNodes,
                language,
                labels,
                true,
                showModificationDate,
                showConnectedNodesList,
                showSocialMediaPanel,
                preprocessorOptions
            );

            const marginalStyle = KontextMap._getPopupStyle(
                popupDesign,
                'marginal'
            );
            contentElem
                .querySelectorAll('.popup-content-marginal')
                .forEach((el) => applyStyleSet(el, marginalStyle));

            hydrateLottieFrames(contentElem);
        }

        const setExternalContent = (html, preprocessorOptions = {}) => {
            clearContent();
            renderExternalContent(contentElem, html, preprocessorOptions);
        };

        const setSocialMedia = (popupData) => {
            if (!showSocialMediaPanel) return;
            const socialMediaElem = contentElem.querySelector('.popup-social');
            if (!socialMediaElem) return;

            renderSocialPanel(
                socialMediaElem,
                socialMediaServices,
                socialMediaMapUrl,
                popupData,
                language,
                labels
            );
        };

        /**
         * Set previous/next navigation for a popup node.
         * @param {Node} popupData
         */
        const setNavigation = (popupData) => {
            const nav = popupData.content?.navigation || {};
            const prevItem = getNode(nav.previous_id);
            const nextItem = getNode(nav.next_id);
            buildPrevNextNav(
                bottomNavElem,
                prevItem,
                nextItem,
                language,
                unwrapText
            );
        };

        const SIZE_TRANSITION_TIME = 0;
        let sizeTransitionTimeout;

        /**
         * Position the panel and center the map to the relevant area.
         * @param {Function} [callback]
         * @param {{zoomLevel?: number}} [options]
         */
        this.position = (callback, options) => {
            const opts = options || {};
            if (!manager.currentSource) return;

            if (sizeTransitionTimeout) clearTimeout(sizeTransitionTimeout);
            sizeTransitionTimeout = setTimeout(() => {
                if (!manager.currentSource) return;

                /** @type {HTMLElement} */
                const source = manager.currentSource;
                const node = source.__data__;
                const targetZoomLevel = limit(
                    Object.prototype.hasOwnProperty.call(opts, 'zoomLevel')
                        ? opts.zoomLevel
                        : map.zoomLevel(),
                    node.visibility.min_level,
                    node.visibility.max_level
                );

                let modelTargetX;
                let modelTargetY;

                const isLandscape =
                    getComputedStyle(panelElem).flexDirection !== 'column';
                if (isLandscape) {
                    const adjNodeIds = Array.from(adjacently(String(node.id)));
                    const adjacentNodes = showConnectedNodesHighlighted
                        ? adjNodeIds
                              .map((id) => getNode(id))
                              .filter((adj) => {
                                  const v = adj.visibility;
                                  return (
                                      v.min_level <= targetZoomLevel &&
                                      v.max_level >= targetZoomLevel
                                  );
                              })
                        : [];

                    let minX;
                    let minY;
                    let maxX;
                    let maxY;
                    for (const connected of [node, ...adjacentNodes]) {
                        const dims =
                            KontextMap.getNodeDimensions(connected);
                        const minx = dims.center.x - dims.size.width_2;
                        const maxx = dims.center.x + dims.size.width_2;
                        const miny = dims.center.y - dims.size.height_2;
                        const maxy = dims.center.y + dims.size.height_2;
                        if (minX === undefined || minx < minX) minX = minx;
                        if (maxX === undefined || maxx > maxX) maxX = maxx;
                        if (minY === undefined || miny < minY) minY = miny;
                        if (maxY === undefined || maxy > maxY) maxY = maxy;
                    }
                    const centerX = (minX + maxX) / 2;
                    const centerY = (minY + maxY) / 2;

                    const targetScale = map.scaleForZoomLevel(targetZoomLevel);
                    const rect = panelElem.getBoundingClientRect();
                    const popupWidthModel =
                        (rect.left + rect.width + window.scrollX) / targetScale;

                    modelTargetX = centerX - popupWidthModel / 2;
                    modelTargetY = centerY;
                } else {
                    const dims = KontextMap.getNodeDimensions(node);
                    modelTargetX = dims.center.x;
                    modelTargetY = dims.center.y;
                }

                map.centerTo(modelTargetX, modelTargetY, targetZoomLevel, true);
                if (callback) callback();
            }, SIZE_TRANSITION_TIME);
        };

        /** Re-render social media panel for the current popup node. */
        this.reloadSocialMedia = () => {
            if (!manager.currentSource) return;
            const node = manager.currentSource.__data__;
            setSocialMedia(node);
        };

        /**
         * Set the base URL used to build share links.
         * @param {string} url
         */
        this.setSocialMediaBaseURL = (url) => {
            socialMediaMapUrl = url;
        };

        const TRANSITION_TIME_MILLIS = 1000;
        let transitionTimer;

        /**
         * Show the panel for a given node "source" element.
         * @param {HTMLElement} source
         * @param {{externalContent?: string, popupType?: string, zoomLevel?: number}} [options]
         */
        this.showFor = function (source, options = {}) {
            if (!source) return;

            const node = source.__data__;
            if (transitionTimer) clearTimeout(transitionTimer);

            if (options.externalContent) {
                const url = encodeURI(options.externalContent);
                setExternalContent(
                    `<iframe frameborder="0" src="${url}"></iframe>`
                );
            } else {
                setPopupContent(node);
                if (showSocialMediaPanel) setSocialMedia(node);
            }
            setNavigation(node);

            if (currentPanelType) {
                panelElem.classList.remove(currentPanelType);
                currentPanelType = undefined;
            }
            if (options.popupType) {
                currentPanelType = options.popupType;
                panelElem.classList.add(currentPanelType);
            }

            const designType = popupTypeMap.get(options.popupType) || 'small';
            const styleSet = KontextMap._getPopupStyle(
                popupDesign,
                designType
            );

            contentElem
                .querySelectorAll(':scope > h1, :scope > h2')
                .forEach((h) => {
                    if (styleSet.cssProps.color)
                        h.style.color = styleSet.cssProps.color;
                    if (styleSet.cssProps.fontFamily)
                        h.style.fontFamily = styleSet.cssProps.fontFamily;
                });
            const main = contentElem.querySelector('.popup-content-main');
            if (main) applyStyleSet(main, styleSet);

            const connectedNodeIds =
                (showConnectedNodesHighlighted
                    ? adjacently(String(node.id))
                    : null) || [];
            map.highlight(new Set([String(node.id), ...connectedNodeIds]));

            map.nodeActive(source, true);

            manager.currentSource = source;
            manager.sourceId = node.id;

            manager.position(undefined, options);

            if (!this.isVisible()) {
                panelElem.classList.add('active', 'transitioning');
                const endEvt =
                    browser?.transitionEndEvent || 'transitionend';
                panelElem.addEventListener(
                    endEvt,
                    function onEnd() {
                        panelElem.classList.remove('transitioning');
                        panelElem.removeEventListener(endEvt, onEnd);
                    },
                    { once: true }
                );
            }

            nameDialog(node);

            // Only on a deliberate open. Restoring a deep link on page load
            // must not yank focus away from the top of the document.
            if (options.moveFocus) {
                // The panel lives inside the clipped map container, and it
                // is still sliding in when this runs. Letting the browser
                // reveal it scrolls that container, which drags the map's
                // chrome along with it; the panel arrives on its own.
                sectionElem.focus({ preventScroll: true });
                movedFocusIn = true;
            }

            emit(manager, 'show.popup', [source, options]);
        };

        /**
         * Give the dialog an accessible name.
         *
         * The heading is preferred, but popup content is author-supplied and
         * a point may have no title at all, so fall back to a label rather
         * than leaving the dialog anonymous.
         */
        function nameDialog(node) {
            const heading = contentElem.querySelector('h1, h2');
            if (heading) {
                if (!heading.id) {
                    heading.id = 'popup-panel-title';
                }
                sectionElem.setAttribute('aria-labelledby', heading.id);
                sectionElem.removeAttribute('aria-label');
                return;
            }
            sectionElem.removeAttribute('aria-labelledby');
            const title = stripHTML(
                translate(node?.content?.title, language) || ''
            ).trim();
            sectionElem.setAttribute(
                'aria-label',
                title || labels.a11y?.unnamedPoint || ''
            );
        }

        /** @returns {boolean} True if the panel is visible. */
        this.isVisible = () => panelElem.classList.contains('active');

        /** @returns {boolean} True if the panel is currently transitioning. */
        this.isInTransition = () =>
            panelElem.classList.contains('transitioning');

        /** Hide the panel and clear selection highlight. */
        this.hide = () => {
            if (!this.isVisible()) return;

            // Captured before the manager clears it below.
            const source = manager.currentSource;

            panelElem.classList.remove('active');

            transitionTimer = setTimeout(() => {
                clearContent();
            }, TRANSITION_TIME_MILLIS);

            map.highlight();
            map.nodeActive(manager.currentSource, false);

            manager.currentSource = undefined;
            manager.sourceId = undefined;

            if (movedFocusIn) {
                movedFocusIn = false;
                // The point may have left the current zoom range while the
                // panel was open, in which case it is no longer focusable.
                a11y?.restoreFocus(source, viewport);
            }
        };

        // init
        createPanel();
    };

    /**
     * Manager for detached content boxes.
     * Public methods: showFor, hide, reloadSocialMedia, setSocialMediaBaseURL.
     *
     * @param {HTMLElement} viewportElem
     * @param {MapManager} map
     * @param {MapElements} mapData
     * @param {string} language
     * @param {Object} labels
     * @param {Object} options
     * @constructor
     */
    KontextMap.PopupManager = function (
        viewportElem,
        map,
        mapData,
        language,
        labels,
        options
    ) {
        const manager = /** @type {any} */ (this);

        const popupDesign = mapData.design?.popups || {};
        const popupTypeMap = new Map([
            ['popup-large', 'detached'],
            ['popup-medium', 'detached_medium'],
            ['popup-small', 'detached_small']
        ]);

        /** @type {HTMLElement} */
        const viewport = viewportElem;
        /** @type {HTMLElement} */
        const model = viewport.querySelector('.map-model');
        const relativeOffsetFromCenterMap = new Map([
            ['detached', 0.3],
            ['detached_medium', 0.2],
            ['detached_small', 0.1]
        ]);

        const showConnectedNodesHighlighted = JSON.parse(
            options.showConnectedNodesHighlighted
        );
        const showConnectedNodesList = JSON.parse(
            options.showConnectedNodesList
        );

        const showSocialMediaPanel = getSocial()?.isEnabled();
        const socialMediaServices = showSocialMediaPanel
            ? getSocial().enabledServices()
            : [];
        let socialMediaMapUrl =
            window.location.origin + window.location.pathname;

        const showModificationDate = JSON.parse(options.showModificationDate);

        /** @type {HTMLElement | null} */
        let currentPopup = null;
        let releaseTrap = null;

        const adjacentNodesById = KontextMap._adjacencyMap(mapData);

        const nodesById = new Map();
        (mapData.nodes || []).forEach((n) => nodesById.set(String(n.id), n));

        function getNode(nodeId) {
            return nodesById.get(String(nodeId));
        }
        function unwrapText(text) {
            return smartStripLineBreaks(text);
        }

        function createPopup() {
            const popup = document.createElement('div');
            popup.className = 'popup popup-detached';
            popup.setAttribute('data-cc-external', 'placeholder');

            const section = document.createElement('section');
            // Modal, unlike the side panel: this popup is appended inside the
            // map model itself, so without aria-modal and a trap the reader
            // and the Tab key both walk straight into the points rendered
            // underneath it.
            section.setAttribute('role', 'dialog');
            section.setAttribute('aria-modal', 'true');
            section.setAttribute('tabindex', '-1');
            popup.appendChild(section);
            ['mousedown', 'wheel', 'touchstart'].forEach((evt) =>
                section.addEventListener(evt, (e) => e.stopPropagation())
            );

            // `_createPopupMarkup` is called with showTitle=false for this
            // variant, so the dialog would otherwise have no name at all.
            const heading = document.createElement('h2');
            heading.className = 'sr-only';
            section.appendChild(heading);

            const topNav = document.createElement('nav');
            topNav.className = 'popup-navigation-top';
            section.appendChild(topNav);

            const back = document.createElement('button');
            back.type = 'button';
            back.textContent = labels.back;
            back.addEventListener('click', (e) => {
                e.preventDefault();
                manager.hide();
            });
            topNav.appendChild(back);

            const content = document.createElement('main');
            content.className = 'popup-content';
            section.appendChild(content);

            const bottomNav = document.createElement('nav');
            bottomNav.className = 'popup-navigation-bottom';
            applyStyleSet(
                bottomNav,
                KontextMap._getPopupStyle(popupDesign, 'nav')
            );
            section.appendChild(bottomNav);

            const aside = document.createElement('aside');
            const arrow = document.createElement('div');
            arrow.className = 'popup-arrow';
            aside.appendChild(arrow);
            popup.appendChild(aside);

            // attach references
            popup._section = section;
            popup._heading = heading;
            popup._topNav = topNav;
            popup._content = content;
            popup._bottomNav = bottomNav;

            return popup;
        }

        function clearContent(popupEl) {
            const content = /** @type {HTMLElement} */ (popupEl._content);
            stopAudioVideo(content);
            content.scrollTop = 0;
            content.classList.remove('popup-external-content');
            content.innerHTML = '';
        }

        function setPopupContent(popupEl, data, preprocessorOptions = {}) {
            clearContent(popupEl);
            const content = /** @type {HTMLElement} */ (popupEl._content);
            const connectedNodes = Array.from(
                adjacentNodesById.get(String(data.id)) || new Set()
            ).map(getNode);

            KontextMap._createPopupMarkup(
                content,
                data,
                connectedNodes,
                language,
                labels,
                false,
                showModificationDate,
                showConnectedNodesList,
                showSocialMediaPanel,
                preprocessorOptions
            );

            const marginalStyle = KontextMap._getPopupStyle(
                popupDesign,
                'marginal'
            );
            content
                .querySelectorAll('.popup-content-marginal')
                .forEach((el) => applyStyleSet(el, marginalStyle));

            hydrateLottieFrames(content);
        }

        const setExternalContent = (
            popupEl,
            html,
            preprocessorOptions = {}
        ) => {
            const content = /** @type {HTMLElement} */ (popupEl._content);
            clearContent(popupEl);
            renderExternalContent(content, html, preprocessorOptions);
        };

        const setSocialMedia = (popupEl, popupData) => {
            if (!showSocialMediaPanel) return;
            const content = /** @type {HTMLElement} */ (popupEl._content);
            const socialMediaElem = content.querySelector('.popup-social');
            if (!socialMediaElem) return;

            renderSocialPanel(
                socialMediaElem,
                socialMediaServices,
                socialMediaMapUrl,
                popupData,
                language,
                labels
            );
        };

        const setNavigation = (popupEl, popupData) => {
            const nav = popupData.content?.navigation || {};
            const prev = getNode(nav.previous_id);
            const next = getNode(nav.next_id);
            buildPrevNextNav(
                /** @type {HTMLElement} */ (popupEl._bottomNav),
                prev,
                next,
                language,
                unwrapText
            );
        };

        /** Re-render social media panel for the current popup node. */
        this.reloadSocialMedia = () => {
            if (!currentPopup || !manager.currentSource) return;
            const node = manager.currentSource.__data__;
            setSocialMedia(currentPopup, node);
        };

        /**
         * Set the base URL used to build share links.
         * @param {string} url
         */
        this.setSocialMediaBaseURL = (url) => {
            socialMediaMapUrl = url;
        };

        const TRANSITION_TIME_MILLIS = 1000;

        /**
         * Show a detached popup for the given node "source" element.
         * @param {HTMLElement} source
         * @param {{externalContent?: string, popupType?: string, zoomLevel?: number}} [options]
         */
        this.showFor = (source, options = {}) => {
            if (!source) return;

            const node = source.__data__;
            this.hide();

            const targetZoomLevel = limit(
                Object.prototype.hasOwnProperty.call(options, 'zoomLevel')
                    ? options.zoomLevel
                    : map.zoomLevel(),
                node.visibility.min_level,
                node.visibility.max_level
            );
            const targetScale = map.scaleForZoomLevel(targetZoomLevel);
            const inverseScale = 1 / targetScale;

            const nodeDims = KontextMap.getNodeDimensions(node);

            const designType =
                popupTypeMap.get(options.popupType) || 'detached';
            const styleSet = KontextMap._getPopupStyle(
                popupDesign,
                designType
            );

            currentPopup = createPopup();
            applyStyleSet(
                /** @type {HTMLElement} */ (currentPopup._content),
                styleSet
            );

            currentPopup.style.transform = `translateX(-0.25rem) translate(-50%, -100%) scale(${inverseScale})`;
            currentPopup.style.left = `${numStr(nodeDims.center.x)}px`;
            currentPopup.style.top = `${numStr(nodeDims.center.y - nodeDims.size.height_2)}px`;

            model.appendChild(currentPopup);

            manager.currentSource = source;
            manager.sourceId = node.id;

            map.nodeActive(source, true);
            const connectedNodeIds = showConnectedNodesHighlighted
                ? adjacentNodesById.get(String(node.id)) || new Set()
                : new Set();
            map.highlight(new Set([String(node.id), ...connectedNodeIds]));

            const rel = relativeOffsetFromCenterMap.get(designType) || 0;
            const viewportHeight = viewport.clientHeight;

            function setMobileHeight() {
                currentPopup.style.height = '';
            }
            const mm = window.matchMedia(
                'only screen and (max-width: 480px), only screen and (max-height: 480px)'
            );
            setMobileHeight();
            mm.addEventListener
                ? mm.addEventListener('change', setMobileHeight)
                : mm.addListener?.(setMobileHeight);

            const modelTargetX = nodeDims.center.x;
            const modelTargetY =
                nodeDims.center.y - rel * viewportHeight * (1 / targetScale);
            map.centerTo(modelTargetX, modelTargetY, targetZoomLevel, true);

            const headingId = `popup-detached-title-${node.id}`;
            currentPopup._heading.id = headingId;
            currentPopup._heading.textContent =
                stripHTML(
                    translate(node.content?.title, language) || ''
                ).trim() ||
                labels.a11y?.unnamedPoint ||
                '';
            currentPopup._section.setAttribute('aria-labelledby', headingId);

            setTimeout(() => {
                currentPopup?.classList.add('active');
                emit(manager, 'show.popup', [source]);
            }, 0);

            if (options.moveFocus) {
                releaseTrap = a11y?.trapFocus(
                    currentPopup._section,
                    {
                        modal: true,
                        initialFocus: currentPopup._section,
                        returnFocus: source,
                        onEscape: () => manager.hide()
                    }
                );
            }

            if (options.popupType)
                currentPopup.classList.add(options.popupType);

            if (options.externalContent) {
                const url = encodeURI(options.externalContent);
                setExternalContent(
                    currentPopup,
                    `<iframe frameborder="0" src="${url}"></iframe>`
                );
            } else {
                setPopupContent(currentPopup, node);
                if (showSocialMediaPanel) setSocialMedia(currentPopup, node);
            }
            setNavigation(currentPopup, node);
        };

        /** Hide and remove the current detached popup, clearing highlights. */
        this.hide = () => {
            if (!currentPopup) return;

            // Synchronously: the element is only removed a second later, and
            // leaving the trap installed until then keeps Tab captive inside
            // a popup that is already fading out.
            releaseTrap?.();
            releaseTrap = null;

            map.highlight();
            map.nodeActive(manager.currentSource, false);

            manager.currentSource = undefined;
            manager.sourceId = undefined;

            stopAudioVideo(
                /** @type {HTMLElement} */ (currentPopup._content)
            );

            currentPopup.classList.remove('active');
            const p = currentPopup;
            currentPopup = null;

            setTimeout(() => {
                p.remove();
            }, TRANSITION_TIME_MILLIS);
        };
    };
})();
